import { useMemo } from "react";
import { useRoutes } from "react-router-dom";
import { Provider } from "react-redux";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { CacheProvider } from "@emotion/react";

import { Box, CssBaseline } from "@mui/material";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import "./i18n";
import createAppTheme from "./theme";
import routes from "./routes";

import useColorMode from "./hooks/useColorMode";
import { store } from "./redux/store";
import createEmotionCache from "./utils/createEmotionCache";
import { PublicClientApplication } from "@azure/msal-browser";
import {
  AuthenticatedTemplate,
  MsalProvider,
  UnauthenticatedTemplate,
} from "@azure/msal-react";

import GlobalStyle from "./components/GlobalStyle";
import EnvBanner from "./components/shell/EnvBanner";
import { ToastProvider } from "./components/ui/ToastProvider";
import LoginPage from "./features/Auth/Login";

interface AppProps {
  instance: PublicClientApplication;
  emotionCache?: any;
}

const clientSideEmotionCache = createEmotionCache();

const WrappedView = ({
  content,
  emotionCache,
}: {
  content: React.ReactNode;
  emotionCache: any;
}) => {
  const { resolvedMode } = useColorMode();
  const theme = useMemo(() => createAppTheme(resolvedMode), [resolvedMode]);

  // Theme, baseline, title and banner sit above the MSAL templates so the sign-in
  // page gets the same look, tab title and environment banner as the app.
  return (
    <CacheProvider value={emotionCache}>
      <HelmetProvider>
        <Helmet titleTemplate="%s | CSI Pay" defaultTitle="CSI Pay" />
        <MuiThemeProvider theme={theme}>
          <CssBaseline enableColorScheme />
          <GlobalStyle />
          <ToastProvider>
            <Box
              className="App"
              sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <EnvBanner />
              <AuthenticatedTemplate>
                <Provider store={store}>
                  {/* @ts-ignore */}
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    {content}
                  </LocalizationProvider>
                </Provider>
              </AuthenticatedTemplate>
              <UnauthenticatedTemplate>
                <LoginPage />
              </UnauthenticatedTemplate>
            </Box>
          </ToastProvider>
        </MuiThemeProvider>
      </HelmetProvider>
    </CacheProvider>
  );
};

export let msalInstance: PublicClientApplication;

const App = ({ instance, emotionCache = clientSideEmotionCache }: AppProps) => {
  msalInstance = instance; // Store the instance
  const content = useRoutes(routes);
  return (
    <MsalProvider instance={instance}>
      <WrappedView content={content} emotionCache={emotionCache} />
    </MsalProvider>
  );
};

export default App;
